import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormsServiceService } from '../../../api/services/forms-service.service';

@Component({
    selector: 'app-domain-with-tags',
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    templateUrl: './domain-with-tags.component.html',
    styleUrl: './domain-with-tags.component.css'
})
export class DomainWithTagsComponent implements OnInit {
  domainInput: string = '';
  inputWithTags: any[] = [];
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() formData: any;
  @Input() autoFocused: any;
  showError: string = '';
  errors: { [key: string]: string } = {};
  @ViewChild('focusInput') focusInput!: ElementRef<HTMLInputElement>;
  @Output() submitRequested = new EventEmitter<void>();

  constructor(private formService: FormsServiceService) {}

  ngOnInit(): void {
    const control = this.formGroup?.get(this.fields.fieldName);
    if (control && Array.isArray(control.value)) {
      this.inputWithTags = [...control.value];
    }
  }

  ngOnChanges() {
    if (!this.autoFocused) return;

    setTimeout(() => {
      this.focusInput?.nativeElement.focus();
    });
  }
  addDomainFromControl(inputRef: HTMLInputElement): void {
    const inputValue = inputRef.value;
    const arrayControl = this.formGroup.get(this.fields.fieldName);
    const tagsControl = this.formGroup.get(this.fields.fieldName + 'Tags');

    // Get regex from validationRules
    const regexRule = this.fields.validationRules?.find(
      (v: any) => v.type.toLowerCase() === 'regex',
    );
    const domainRegex = regexRule ? new RegExp(regexRule.value) : null;

    if (inputValue && inputValue.trim()) {
      this.showError = '';
      const domainList = inputValue
        .split(',')
        .map((d: string) => d.trim())
        .filter((d: string) => d);

      domainList.forEach((domain) => {
        const normalizedDomain = domain.toLowerCase();
        // ✅ 1. Validate against the regex from validationRules
        if (domainRegex && !domainRegex.test(normalizedDomain)) {
          this.showError = regexRule?.error || 'Invalid domain format.';
          return;
        }

        // ✅ 2. Skip duplicates
        if (this.inputWithTags.includes(normalizedDomain)) {
          return;
        }

        // ✅ 3. Proceed with API check
        const payload = {
          formId: this.fields.formId,
          fields: ['name', 'domain', 'subnodes.domain'],
          value: normalizedDomain,
        };

        this.formService.fieldAvailabilityChecker(payload).subscribe({
          next: (res: any) => {
            if (res.success) {
              this.inputWithTags.push(normalizedDomain);
              arrayControl?.markAsDirty();
              arrayControl?.markAsTouched();
              arrayControl?.setValue([...this.inputWithTags]);
              
              tagsControl?.markAsDirty();
              tagsControl?.markAsTouched();
              tagsControl?.setValue([...this.inputWithTags]);
              
              this.showError = '';
              inputRef.value = '';
              this.domainInput = '';
            }
          },
          error: (err) => {
            if (err.error?.data?.exists) {
              this.showError = err.error.message || 'Domain already exists';
            }
          },
        });
      });
    }
    inputRef.value = '';
    this.domainInput = '';
  }

  removeDomain(index: number): void {
    this.inputWithTags.splice(index, 1);
    const control = this.formGroup.get(this.fields.fieldName);
    control?.markAsDirty();
    control?.markAsTouched();
    control?.setValue([...this.inputWithTags]);

    this.showErrorMessage();
  }

  showErrorMessage(): boolean {
    const control = this.formGroup.get(this.fields.fieldName);
    if (!control) return false;

    if (control.hasError('required') && this.inputWithTags.length === 0) {
      this.showError = 'Please add at least one domain';
      return false;
    }

    if (control.hasError('pattern') && control.dirty) {
      const regexRule = this.fields.validationRules?.find(
        (v: any) => v.type.toLowerCase() === 'regex',
      );
      this.showError = regexRule?.error || 'Invalid format.';
      return false;
    }

    this.showError = '';
    return true;
  }

  /** Function to show the validation error message on enter of the text */
  onKeyUpValidateFields(field: any): void {
    const fieldName = field.fieldName;
    const control = this.formGroup.get(fieldName);
    if (!control) return;
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
    this.errors[fieldName] = '';

    if (control.hasError('pattern')) {
      const regexRule = field.validationRules?.find(
        (v: any) => v.type.toLowerCase() === 'regex',
      );
      this.errors[fieldName] = regexRule?.error || 'Invalid format.';
      return;
    }

    this.errors[fieldName] = '';
  }

  onEnter(inputRef: HTMLInputElement, event: any): void {
    event.preventDefault();
    event.stopPropagation();

    const value = inputRef.value.trim();

    //  Enter WITH text → add tag
    if (value) {
      this.addDomainFromControl(inputRef);
      return;
    }

    //  Enter with NO text but tags exist → submit modal
    if (this.inputWithTags.length > 0) {
      this.submitRequested.emit();
      return;
    }

    //  Enter with nothing → show error
    this.showError = 'Please add at least one domain';
  }
}
